import type { UserPublic } from '@repo/types/users';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import type { BaseSyntheticEvent } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { useGetUsersInfinite } from '@repo/api/user/useGetUsersInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { cn } from '@repo/lib/cn';
import { countStudents, getStudentIdsOverSlots } from '@repo/lib/user-lvl/countStudents';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { ChevronLeftIcon, InfoIcon, PlusIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { Input, NumberInputHorizontal } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/input';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tag } from '@/components/ui/tags';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getAvatar } from '@/utils/getAvatar';

export type GroupFormTab = 'main' | 'selectedList';
type UsersErrorMessage = ReturnType<typeof useGetUsersInfinite>['errorMessage'];

export const useGroupUsersFormState = () => {
  const [tabs, setTabs] = useState<GroupFormTab>('main');
  const [searchUserValue, setSearchUserValue] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<UserPublic[]>([]);

  const searchUserDebouncedValue = useDebounceValue(searchUserValue, 400);
  const getUsersInfinite = useGetUsersInfinite(searchUserDebouncedValue);
  const usersErrorMessage = getUsersInfinite.errorMessage;
  const { data: users, isEmpty: usersIsEmpty, noResults: usersNoResults } = useInfiniteQueryResult(getUsersInfinite.data, searchUserDebouncedValue);

  const isMainTab = tabs === 'main';

  const onRemoveUser = (id: number) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== id));
  };

  const onTabsValueChange = (tab: string) => {
    setTabs(tab === 'selectedList' ? 'selectedList' : 'main');
  };

  const setSelectedUsersFromIds = (ids: string[]) => {
    const selectedIds = new Set(ids.map(Number));

    setSelectedUsers((prev) => {
      const existing = prev.filter((user) => selectedIds.has(user.id));
      const newUsers = users.filter((user) => selectedIds.has(user.id) && !existing.some((existingUser) => existingUser.id === user.id));

      return [...existing, ...newUsers];
    });
  };

  const resetState = () => {
    setTabs('main');
    setSearchUserValue('');
    setSelectedUsers([]);
  };

  return {
    tabs,
    setTabs,
    isMainTab,
    searchUserValue,
    setSearchUserValue,
    selectedUsers,
    setSelectedUsers,
    users,
    usersIsEmpty,
    usersNoResults,
    usersErrorMessage,
    getUsersInfinite,
    onRemoveUser,
    onTabsValueChange,
    setSelectedUsersFromIds,
    resetState,
  };
};

interface GroupDialogHeaderProps {
  isMainTab: boolean;
  title: string;
  description: string;
  searchUserValue: string;
  onSearchValueChange: (value: string) => void;
  onBackToMain: () => void;
}

export function GroupDialogHeader(groupDialogHeaderProps: GroupDialogHeaderProps) {
  const { isMainTab, title, description, onSearchValueChange, onBackToMain } = groupDialogHeaderProps;

  if (isMainTab) {
    return (
      <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
        <FeaturedIcon size={'lg'}>
          <UsersIcon />
        </FeaturedIcon>
        <div className={'flex min-w-0 grow flex-col gap-0.5'}>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className={'text-muted-foreground text-xs'}>{description}</DialogDescription>
        </div>
      </DialogHeader>
    );
  }

  return (
    <DialogHeader>
      <DialogTitle className={'sr-only'}>{title}</DialogTitle>
      <Button variant={'outline'} onClick={onBackToMain}>
        <ChevronLeftIcon />
      </Button>
      <SearchInput setValue={onSearchValueChange} />
    </DialogHeader>
  );
}

interface SelectedUsersSummaryProps {
  selectedUsers: UserPublic[];
  slotsValue: number | undefined;
  onRemoveUser: (id: number) => void;
  onOpenSelector: () => void;
}

export function SelectedUsersSummary(selectedUsersSummaryProps: SelectedUsersSummaryProps) {
  const { selectedUsers, slotsValue, onRemoveUser, onOpenSelector } = selectedUsersSummaryProps;
  const studentsCount = countStudents(selectedUsers);
  const studentIdsOverSlots = getStudentIdsOverSlots(selectedUsers, slotsValue);
  return (
    <div className="space-y-3">
      <div className={'flex justify-between'}>
        <h5 className={'font-medium'}>Użytkownicy:</h5>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild className={cn(!(slotsValue !== undefined && studentsCount > slotsValue) && 'hidden', 'cursor-pointer')}>
              <InfoIcon className="text-warning size-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-50">
              Zaznaczeni użytkownicy grupy podkreśleni poniżej przekraczają zalecany limit miejsc. Liczba miejsc jest opcjonalna i ma charakter informacyjny.
            </TooltipContent>
          </Tooltip>
          <p
            className={cn(
              'text-muted-foreground my-auto min-w-28 text-right text-xs font-medium whitespace-nowrap tabular-nums',
              slotsValue !== undefined && studentsCount > slotsValue && 'text-warning',
              slotsValue === undefined && 'hidden'
            )}
          >
            Liczba miejsc: {studentsCount} / {slotsValue}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {selectedUsers.map(({ id, name, surname, avatarURL }) => (
          <Tag key={id} onRemove={() => onRemoveUser(id)} variant={studentIdsOverSlots.has(id) ? 'warning' : 'muted'} border>
            <Avatar size={'xs'}>
              <AvatarImage src={getAvatar(avatarURL)} alt={`${name}_${surname}`} />
              <AvatarFallback>
                <UserIcon />
              </AvatarFallback>
            </Avatar>
            {name} {surname.at(0)}.
          </Tag>
        ))}

        {selectedUsers.length > 0 ? (
          <Badge variant="muted" border className={'cursor-pointer'} onClick={onOpenSelector} asChild>
            <button type="button">
              <PlusIcon />
              Dodaj
            </button>
          </Badge>
        ) : (
          <EmptyState className="border-border-secondary bg-muted/35 w-full rounded-md border pt-4!">
            <EmptyStateContent>
              <EmptyStateTitle className="text-sm!">Brak wybranych użytkowników</EmptyStateTitle>
              <EmptyStateDescription className="max-w-xs text-xs!">Wybierz użytkowników którzy będą członkami grupy</EmptyStateDescription>
              <EmptyStateFooter>
                <Button type={'button'} variant={'outline'} size="sm" onClick={onOpenSelector}>
                  <PlusIcon />
                  Dodaj użytkowników
                </Button>
              </EmptyStateFooter>
            </EmptyStateContent>
          </EmptyState>
        )}
      </div>
    </div>
  );
}

interface UsersSelectListProps {
  selectedUsers: UserPublic[];
  onSelectionChange: (ids: string[]) => void;
  users: UserPublic[];
  usersErrorMessage: UsersErrorMessage;
  usersIsEmpty: boolean;
  usersNoResults: boolean;
  getUsersInfinite: UseInfiniteQueryResult<InfiniteData<UserPublic[], unknown> | undefined, Error>;
}

export function UsersSelectList(usersSelectListProps: UsersSelectListProps) {
  const { selectedUsers, onSelectionChange, users, usersErrorMessage, usersIsEmpty, usersNoResults, getUsersInfinite } = usersSelectListProps;
  return (
    <SelectList type={'multiple'} selected={selectedUsers.map((user) => String(user.id))} setSelected={onSelectionChange}>
      <SelectListInfiniteQueryStates
        query={getUsersInfinite}
        isEmpty={usersIsEmpty}
        noResults={usersNoResults}
        isEmptyMessage={'Wyszukaj użytkownika'}
        noResultMessage={'Nie znaleziono użytkownika'}
        errorMessage={usersErrorMessage}
      />

      {users.map((user) => (
        <SelectListElement key={user.id} id={String(user.id)}>
          <Avatar size={'sm'}>
            <AvatarImage src={getAvatar(user.avatarURL)} alt={user.name} />
            <AvatarFallback>{user.name[0] ?? <UserIcon />}</AvatarFallback>
          </Avatar>
          <SelectListElementContent>
            <SelectListElementTitle>
              {user.name} {user.surname}
            </SelectListElementTitle>
            <SelectListElementDescription>{getUserLvl(user.lvl)}</SelectListElementDescription>
          </SelectListElementContent>

          <SelectListElementTrigger>
            {({ selected, toggleElement }) => (
              <Checkbox
                checked={selected}
                onCheckedChange={() => {
                  toggleElement();
                }}
                className="after:absolute after:inset-0"
              />
            )}
          </SelectListElementTrigger>
        </SelectListElement>
      ))}
      <InfiniteScroll hasNextPage={getUsersInfinite.hasNextPage} isLoading={getUsersInfinite.isLoading} isFetching={getUsersInfinite.isFetching} next={getUsersInfinite.fetchNextPage} threshold={0.65}>
        {getUsersInfinite.hasNextPage ? <SelectListElementSkeleton /> : null}
      </InfiniteScroll>
    </SelectList>
  );
}

interface SlotsCounterProps {
  studentsCount: number;
  slotsValue: number | undefined;
}

export function SlotsCounter(slotsCounterProps: SlotsCounterProps) {
  const { studentsCount, slotsValue } = slotsCounterProps;
  return (
    <p
      className={cn(
        'text-muted-foreground min-w-36 text-start text-xs font-medium whitespace-nowrap tabular-nums',
        slotsValue !== undefined && studentsCount > slotsValue && 'text-warning',
        slotsValue === undefined && 'hidden'
      )}
    >
      Liczba miejsc: {studentsCount} / {slotsValue}
    </p>
  );
}

interface GroupUpsertDetailsFormProps<T extends FieldValues> {
  control: Control<T>;
  nameField: FieldPath<T>;
  descField: FieldPath<T>;
  slotsField: FieldPath<T>;
  onSubmit: (event?: BaseSyntheticEvent) => void | Promise<void>;
  formId: string;
}

export function GroupUpsertDetailsForm<T extends FieldValues>(groupUpsertDetailsFormProps: GroupUpsertDetailsFormProps<T>) {
  const { control, nameField, descField, slotsField, formId, onSubmit } = groupUpsertDetailsFormProps;
  return (
    <form id={formId} className={'flex grow flex-col gap-3'} onSubmit={onSubmit} noValidate>
      <FormField
        name={nameField}
        control={control}
        render={({ field }) => (
          <FormItem className={'col-span-1'}>
            <FormLabel>Nazwa</FormLabel>
            <FormControl>
              <Input {...field} placeholder={'Nazwa'} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name={descField}
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Opis <small className={'text-muted-foreground text-sm'}>(opcjonalne)</small>
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder={'Opis'}
                {...field}
                value={field.value ?? undefined}
                onChange={(event) => {
                  field.onChange(event.target.value ?? null);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name={slotsField}
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              ilość miejsc <small className={'text-muted-foreground text-sm'}>(opcionalne)</small>
            </FormLabel>
            <FormControl>
              <NumberInputHorizontal
                {...field}
                min={1}
                max={1000}
                value={field.value ?? undefined}
                onChange={(value) => {
                  field.onChange(value ?? null);
                }}
                placeholder={'np.15'}
              />
            </FormControl>
            <FormDescription>Podana liczba miejsc jest opcjonalna i ma charakter informacyjny.</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </form>
  );
}
